<script lang="ts">
    import { createGridLayout } from "./gridLayout";
    import { setSubLayout } from "./sublayout";
    import { getLayout } from "../Layout.svelte";

    export let id: string;

    const { layout: editorLayout } = getLayout();

    const {
        minSize,
        areas,
        resize,
        autoSize,
        collapsed,
        collapse,

        state
    } = createGridLayout(id, editorLayout);

    setSubLayout({
        collapse
    });

    export let gap: number = 2;

    let clazz: string = "";
    export { clazz as class };

    let gridElem: HTMLElement;

    function resizeStart(e: PointerEvent, index: number, dir: number) {
        const elem = e.target as HTMLElement;

        const otherAutoSize = autoSize[dir][(index + 1) % 2] ? 0 : 1;
        let totalSize = 100 - $state.size[dir][(index + 1) % 2] * otherAutoSize;

        const property = dir === 0 ? "grid-template-columns" : "grid-template-rows";
        const values = getComputedStyle(gridElem).getPropertyValue(property).split(" ").map((v) => parseInt(v));

        const rect = gridElem.getBoundingClientRect();
        const containerStart = dir === 0 ? rect.left : rect.top;

        const start = containerStart + index * (values[0] + gap);
        const totalElemSize = values[0 + index] + values[1 + index];

        const min = (index === 0 ? minSize[dir][0] : minSize[dir][1]) / totalElemSize;
        const max = 1 - ((index === 0 ? minSize[dir][1] : minSize[dir][2]) / totalElemSize);

        elem.setPointerCapture(e.pointerId);
        elem.addEventListener("pointermove", resizeMove);
        elem.addEventListener("pointerup", resizeEnd);
        elem.addEventListener("pointercancel", resizeEnd);

        function resizeMove(e: PointerEvent) {
            const ratio = Math.min(Math.max(([e.clientX, e.clientY][dir] - start - gap / 2) / totalElemSize, min), max);

            if (index === 0) {
                $state.size[dir][index] = totalSize * ratio;
            } else {
                $state.size[dir][index] = totalSize * (1 - ratio);
            }
            $state = $state;
        }

        function resizeEnd(e: PointerEvent) {
            elem.removeEventListener("pointermove", resizeMove);
            elem.removeEventListener("pointerup", resizeEnd);
            elem.removeEventListener("pointercancel", resizeEnd);
        }
    }


    const templateArea = areas.map(row => `"${row.join(" ")}"`).join(" ");

    function templateSize(dir: number, values: number[], collapsed: any) {
        function f(index: number) {
            return (autoSize[dir][index] || collapsed[dir][index]) ? "max-content" : `${values[index]}fr`
        }

        const center = 100 - (autoSize[dir][0] ? 0 : values[0]) - (autoSize[dir][1] ? 0 : values[1]);
        return `${f(0)} ${center}fr ${f(1)}`;
    }
</script>

<!----------------------------------------------------------------------------->

<div bind:this={gridElem}
     class="layout {clazz}"
     style="--layout-gap: {gap}px;
grid-template-areas: {templateArea};
grid-template-columns: {templateSize(0, $state.size[0], $collapsed)};
grid-template-rows: {templateSize(1, $state.size[1], $collapsed)};"
>
  <slot />

  {#each resize as handle}
    {#if handle.left}
      <div class="handle left" class:collapsed={$collapsed[0][1]} style="grid-area: {handle.id}" on:pointerdown={(e) => resizeStart(e, 1, 0)} />
    {/if}
    {#if handle.top}
      <div class="handle top" class:collapsed={$collapsed[1][1]} style="grid-area: {handle.id}" on:pointerdown={(e) => resizeStart(e, 1, 1)} />
    {/if}
    {#if handle.right}
      <div class="handle right" class:collapsed={$collapsed[0][0]} style="grid-area: {handle.id}" on:pointerdown={(e) => resizeStart(e, 0, 0)} />
    {/if}
    {#if handle.bottom}
      <div class="handle bottom" class:collapsed={$collapsed[1][0]} style="grid-area: {handle.id}" on:pointerdown={(e) => resizeStart(e, 0, 1)} />
    {/if}
  {/each}
</div>

<!----------------------------------------------------------------------------->

<style>
    :where(.layout) {
        --handle-size: 8px;

        position: relative;
        display: grid;
        gap: var(--layout-gap);

        height: 100%;
        width: 100%;

        & > * {
            min-height: 0;
            min-width: 0;
        }
    }

    .handle {
        position: absolute;
        --offset: calc(-1 * var(--layout-gap));
        --size-out: calc((var(--handle-size) - var(--layout-gap)) / -2);
        cursor: var(--cursor);

        &.collapsed {
            display: none;
        }

        &::before {
            content: "";
            position: absolute;
            inset: 0;
            opacity: 0.5;
        }

        &.right::before, &.left::before {
            left: var(--size-out);
            right: var(--size-out);
        }

        &.top::before, &.bottom::before {
            top: var(--size-out);
            bottom: var(--size-out);
        }

        &.right, &.left {
            width: var(--layout-gap);
            height: 100%;
            --cursor: ew-resize;
        }

        &.right {
            right: var(--offset);
        }

        &.left {
            left: var(--offset);
        }

        &.top, &.bottom {
            width: 100%;
            height: var(--layout-gap);
            --cursor: ns-resize;
        }

        &.top {
            top: var(--offset);
        }

        &.bottom {
            bottom: var(--offset);
        }
    }
</style>
